import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, Link } from "react-router-dom";
import { getData } from "country-list";
import {
  useGetPublicCoursesQuery,
  useRegisterInstructorMutation,
} from "../services/auth/authApi";
import Button from "../components/common/Button";
import FormInput from "../components/common/FormInput";
import { markSignupVerified } from "../store/signupStorage";
import "@fontsource/work-sans/400.css";
import "@fontsource/work-sans/500.css";
import "@fontsource/work-sans/600.css";
import {
  EmergencyContact,
  FilledDropDown,
  GraduationCap,
  LockIcon,
  OrangeProfile,
  OrangeProfileFilled,
  ProfileLocationIcon,
  Upload,
} from "../components/Icons";
import { Plus, X, Eye, EyeOff } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import { useVerifyEmailAddressMutation } from "../services/auth/authApi";
import { AuthNavBar } from "../components/Navbar";

const InstructorRegistration = () => {
  const navigate = useNavigate();
  const countries = getData();

  const [
    registerInstructor,
    { isLoading: isRegisteringInstructor, error: registerInstructorError },
  ] = useRegisterInstructorMutation();
  const {
    data: publicCoursesResponse,
    isLoading: isPublicCoursesLoading,
    isError: isPublicCoursesError,
  } = useGetPublicCoursesQuery();
  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImageError, setProfileImageError] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showVerifiedModal, setShowVerifiedModal] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [otpInfoMessage, setOtpInfoMessage] = useState("");
  const [verifiedMessage, setVerifiedMessage] = useState(
    "Successfully Verified",
  );
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const otpInputsRef = useRef([]);
  const [
    verifyEmailAddress,
    { isLoading: isVerifyingEmail, error: verifyEmailError },
  ] = useVerifyEmailAddressMutation();

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    trigger,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      phoneNumber: "",
      password: "",
      confirmSecPassword: "",
      agreeTerms: false,
    },
  });

  const password = watch("password");
  const confirmSecPassword = watch("confirmSecPassword");
  const email = watch("email");
  const transcriptFile = watch("transcript");

  React.useEffect(() => {
    if (confirmSecPassword) {
      trigger(["password", "confirmSecPassword"]);
    }
  }, [password, confirmSecPassword, trigger]);

  const [currentCourse, setCurrentCourse] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [registerErrorMsg, setRegisterErrorMsg] = useState("");

  const publicCourses = publicCoursesResponse?.data ?? [];
  const selectedCourseTitles = selectedCourses
    .map((courseId) => publicCourses.find((course) => course._id === courseId))
    .filter(Boolean);

  React.useEffect(() => {
    setValue("coursePreferences", selectedCourses, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [selectedCourses, setValue]);

  React.useEffect(() => {
    return () => {
      if (profileImage) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileImage]);

  const onSubmit = async (data) => {
    setRegisterErrorMsg("");

    if (!profileImageFile) {
      setProfileImageError("Profile picture is required");
      return;
    }

    setProfileImageError("");

    try {
      let coursesToSubmit = [...selectedCourses];
      if (
        currentCourse &&
        !coursesToSubmit.includes(currentCourse) &&
        coursesToSubmit.length < 5
      ) {
        coursesToSubmit = [...coursesToSubmit, currentCourse];
        setSelectedCourses(coursesToSubmit);
        setCurrentCourse("");
      }

      if (coursesToSubmit.length === 0) {
        setRegisterErrorMsg("Please select at least one preferred course.");
        return;
      }

      const submittedData = { ...data };
      delete submittedData.confirmSecPassword;
      delete submittedData.agreeTerms;
      delete submittedData.coursePreferences;

      if (typeof submittedData.bio === "string") {
        submittedData.bio = submittedData.bio.trim();
      }

      const transcriptFile = submittedData.transcript?.[0];
      delete submittedData.transcript;

      const instructorPayload = new FormData();
      Object.entries(submittedData).forEach(([key, value]) => {
        if (value instanceof FileList) return;
        instructorPayload.append(key, value ?? "");
      });

      if (profileImageFile) {
        instructorPayload.append("files", profileImageFile);
      }
      if (transcriptFile) {
        instructorPayload.append("files", transcriptFile);
      }

      instructorPayload.append(
        "consentAccepted",
        String(Boolean(data.agreeTerms)),
      );
      instructorPayload.append("role", "instructor");

      coursesToSubmit.forEach((course,index) => {
        instructorPayload.append(`coursePreferences[${index}]`, course);
      });

      const response = await registerInstructor(instructorPayload).unwrap();

      if (response?.success === false) {
        setRegisterErrorMsg(
          response?.message || "Registration failed. Please try again.",
        );
        return;
      }

      setOtpValues(["", "", "", ""]);
      setOtpError("");
      setOtpInfoMessage("");
      setShowVerifiedModal(false);
      setShowOtpModal(true);
    } catch (error) {
      const errorMsg =
        error?.data?.message ||
        error?.message ||
        "Registration failed. Please try again.";
      setRegisterErrorMsg(errorMsg);
      console.error("Instructor profile submission failed", error);
    }
  };

  const onInvalid = () => {
    setRegisterErrorMsg(
      "Please fix the highlighted form errors before submitting.",
    );
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextValues = [...otpValues];
    nextValues[index] = digit;
    setOtpValues(nextValues);
    setOtpError("");

    if (digit && index < 3) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otpValues.join("");
    if (!/^\d{4}$/.test(otpCode)) {
      setOtpError("Please enter a valid 4-digit OTP");
      return;
    }
    if (!email) {
      setOtpError("Email is missing. Please refresh and try again.");
      return;
    }
    try {
      setIsVerifyingOtp(true);
      setOtpError("");
      setOtpInfoMessage("");
      await verifyEmailAddress({ otp: otpCode, email }).unwrap();
      setVerifiedMessage("OTP verified successfully!");
      markSignupVerified();
      setShowOtpModal(false);
      setShowVerifiedModal(true);
    } catch (error) {
      setOtpError(error?.data?.message || "OTP verification failed.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleVerifiedContinue = () => {
    navigate("/login", { replace: true });
  };

  const handleResendOtp = () => {
    setOtpValues(["", "", "", ""]);
    setOtpError("");
    setOtpInfoMessage("A new OTP has been sent.");
    otpInputsRef.current[0]?.focus();
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImageError("");
      const imageUrl = URL.createObjectURL(file);

      setProfileImage((previousImage) => {
        if (previousImage) {
          URL.revokeObjectURL(previousImage);
        }
        return imageUrl;
      });
      setProfileImageFile(file);
    }
  };

  return (
    <div>
      <AuthNavBar />
      <div className="min-h-screen bg-[#EFEFFD] px-4 pt-6 pb-12 sm:px-6 sm:pt-9 sm:pb-16 lg:px-8">
        <div className="mb-5 text-center tracking-[-2%] sm:mb-[26px]">
          <h1 className="font-['Work_Sans'] text-2xl font-semibold leading-tight text-black sm:text-3xl lg:text-[34px] lg:leading-10">
            Join Our Learning Platform
          </h1>
          <p className="mt-2 text-base leading-6 text-[#636363] sm:mt-0 sm:text-lg sm:leading-[26px] lg:text-[22px]">
            Create your account to get started
          </p>
        </div>
        <div className="mx-auto w-full max-w-[1004px]">
          <div className="rounded-[18px] bg-[#F5F5F5] px-4 pt-4 pb-6 shadow-[0_2px_5.7px_0_#00000026] sm:px-6 sm:pt-[22px] sm:pb-7 lg:px-[35px]">
            <div className="mb-5 rounded-xl border border-[#D3D3D3] bg-[#089BC4] px-4 py-3 text-center sm:mb-7 sm:px-6">
              <h2 className="flex items-center justify-center gap-2 text-xl font-medium text-white sm:gap-2.5 sm:text-2xl lg:text-[28px]">
                <GraduationCap /> Instructor
              </h2>
            </div>
            <form noValidate onSubmit={handleSubmit(onSubmit, onInvalid)}>
              <div className="rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-12 lg:pt-11">
                <h3 className="flex items-center gap-2 text-lg font-medium leading-6 text-black sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <OrangeProfileFilled color={"#089BC4"} /> Personal Information
                </h3>

                <label className="block mb-8 mt-1.5 cursor-pointer text-center sm:mb-14">
                  <div
                    className={`relative mx-auto mb-4 flex h-[100px] w-[100px] items-center justify-center rounded-full bg-[#089BC414] ${
                      profileImageError ? "ring-2 ring-[#C62828]" : ""
                    }`}
                  >
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt="Profile"
                        className="h-full w-full rounded-full object-cover"
                      />
                    ) : (
                      <OrangeProfile color="#089BC4" />
                    )}

                    <div className="absolute bottom-0 right-0">
                      <div className="bg-[#089BC4] flex items-center justify-center rounded-full w-[33px] h-[33px] ">
                        <Upload />
                      </div>
                    </div>
                  </div>

                  <p className="text-base font-light leading-5 text-black sm:text-xl">
                    Upload Profile Picture*
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    className="hidden"
                  />
                </label>
                {profileImageError ? (
                  <p className="-mt-6 mb-6 text-center text-sm font-medium text-[#C62828] sm:-mt-10 sm:mb-10">
                    {profileImageError}
                  </p>
                ) : null}

                <div className="grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="firstName"
                    label="First Name*"
                    type="text"
                    placeholder="Enter First Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.firstName?.message}
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                  />

                  <FormInput
                    id="lastName"
                    label="Last Name*"
                    type="text"
                    placeholder="Enter Last Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.lastName?.message}
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                  />

                  <FormInput
                    id="fatherName"
                    label="Father Name*"
                    type="text"
                    placeholder="Enter Father's Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.fatherName?.message}
                    {...register("fatherName", {
                      required: "Father's name is required",
                    })}
                  />
                </div>

                <div className="mt-7 grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="email"
                    label="Email*"
                    type="email"
                    placeholder="Enter email"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.email?.message}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email",
                      },
                    })}
                  />

                  <div className="relative w-full h-full">
                    <label
                      htmlFor="countryCode"
                      className="mb-1.5 block text-base leading-5 text-black sm:text-lg"
                    >
                      Phone No*
                    </label>
                    <Controller
                      name="phoneNumber"
                      control={control}
                      rules={{
                        required: "Phone number is required",
                        validate: {
                          internationalFormat: (value) =>
                            /^\+[1-9]\d{7,14}$/.test(value) ||
                            "Phone number must be in international format (e.g., +923001234567)",
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          country={"pk"}
                          placeholder="Enter Phone no"
                          enableSearch
                          value={value || ""}
                          onChange={(phone) => {
                            const withPlus = phone.startsWith("+")
                              ? phone
                              : `+${phone}`;
                            onChange(withPlus);
                          }}
                          inputProps={{
                            id: "phoneNumber",
                            name: "phoneNumber",
                          }}
                          inputClass=" !h-[46px] !border !border-[#51CFF363] !rounded-lg !w-full !bg-transparent  !text-base  leading-6 !pl-14 text-black placeholder:text-[#8C8CA1] placeholder:font-light focus:!border-[#089BC4] focus:!outline-none"
                          buttonClass="!px-2 !rounded-l-lg "
                          dropdownClass="bg-white"
                        />
                      )}
                    />
                    {errors.phoneNumber ? (
                      <p className="mt-2 text-sm text-[#C62828]">
                        {errors.phoneNumber.message}
                      </p>
                    ) : null}
                  </div>

                  <FormInput
                    id="dateOfBirth"
                    label="Date of Birth*"
                    type="date"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.dateOfBirth?.message}
                    {...register("dateOfBirth", {
                      required: "Date of birth is required",
                    })}
                  />
                </div>

                <div className="mt-7">
                  <label className="block text-base leading-5 text-black sm:text-lg">
                    Bio*
                  </label>
                  <textarea
                    placeholder="Type here..."
                    maxLength={100}
                    className="px-5 mt-1.5 min-h-32 w-full rounded-lg border border-[#51CFF363] bg-transparent py-3  placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none resize-none"
                    {...register("bio", {
                      validate: (value) => {
                        const trimmed = (value ?? "").trim();
                        if (!trimmed) return "Bio is required";
                        if (trimmed.length < 20 || trimmed.length > 100) {
                          return "Bio must be 20–100 characters";
                        }
                        return true;
                      },
                      onBlur: (event) => {
                        const trimmed = event.target.value.trim();
                        if (trimmed !== event.target.value) {
                          setValue("bio", trimmed, { shouldValidate: true });
                        }
                      },
                    })}
                  />
                  {errors.bio && (
                    <p className="text-sm text-[#C62828]">
                      {errors.bio.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-9 lg:pt-11">
                <h3 className="mb-6 flex items-center gap-2 text-lg font-medium leading-6 text-black sm:mb-9 sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <ProfileLocationIcon color="#089BC4" /> Residence Information
                </h3>

                <FormInput
                  id="address"
                  label="Address*"
                  type="text"
                  placeholder="Street address"
                  labelClassName="block text-base text-black sm:text-lg"
                  inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                  errorMessage={errors.address?.message}
                  {...register("address", { required: "Address is required" })}
                />

                <div className="mt-7 grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="city"
                    label="City*"
                    type="text"
                    placeholder="Enter City Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.city?.message}
                    {...register("city", { required: "City is required" })}
                  />

                  <div>
                    <label className="block text-base leading-5 text-black sm:text-lg">
                      Country*
                    </label>
                    <select
                      className={`px-5 mt-1.5 h-[46px] w-full rounded-lg border border-[#51CFF363] bg-transparent   focus:border-[#089BC4] focus:outline-none appearance-none 
                      ${watch("country") ? "text-black" : "text-[#8C8CA1]"}`}
                      {...register("country", {
                        required: "Country is required",
                      })}
                    >
                      <option value="" disabled>
                        Select Country
                      </option>
                      {countries.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.country && (
                    <p className="text-sm text-[#C62828]">
                      {errors.country.message}
                    </p>
                  )}

                  <FormInput
                    id="postalCode"
                    label="Postal Code*"
                    type="text"
                    placeholder="12345"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.postalCode?.message}
                    {...register("postalCode", {
                      required: "Postal code is required",
                    })}
                  />
                </div>
              </div>

              <div className="mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-9 lg:pt-11">
                <h3 className="mb-6 flex items-center gap-2 text-lg font-medium leading-6 text-black sm:mb-9 sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <EmergencyContact color="#089BC4" /> Emergency Contacts
                </h3>

                <div className="grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="fullName"
                    label="Full Name*"
                    type="text"
                    placeholder="Enter Name"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.fullName?.message}
                    {...register("fullName", {
                      required: "Full name is required",
                    })}
                  />

                  <div>
                    <label className="block text-base leading-5 text-black sm:text-lg">
                      Relationship*
                    </label>
                    <select
                      className={`px-5 mt-1.5 h-[46px]  w-full rounded-lg border border-[#51CFF363] bg-transparent  focus:border-[#089BC4] focus:outline-none appearance-none
                        
                        ${watch("relationship") ? "text-black" : "text-[#8C8CA1]"}`}
                      {...register("relationship", {
                        required: "Relationship is required",
                      })}
                      defaultValue={""}
                    >
                      <option value="" disabled hidden>
                        Select relationship
                      </option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Sibling</option>
                      <option value="grandparent">Grandparent</option>
                      <option value="guardian">Guardian</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.relationship && (
                      <p className="text-sm text-[#C62828]">
                        {errors.relationship.message}
                      </p>
                    )}
                  </div>

                  <div className="relative w-full h-full">
                    <label
                      htmlFor="emergencyPhoneNumber"
                      className="mb-1.5 block text-base leading-5 text-black sm:text-lg"
                    >
                      Phone No*
                    </label>
                    <Controller
                      name="emergencyPhoneNumber"
                      control={control}
                      rules={{
                        required: "Phone is required",
                        validate: {
                          internationalFormat: (value) =>
                            /^\+[1-9]\d{7,14}$/.test(value) ||
                            "Phone number must be in international format (e.g., +923001234567)",
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          country={"pk"}
                          enableSearch
                          placeholder="+9231777383"
                          value={value || ""}
                          onChange={(phone) => {
                            const withPlus = phone.startsWith("+")
                              ? phone
                              : `+${phone}`;
                            onChange(withPlus);
                          }}
                          inputProps={{
                            id: "emergencyPhoneNumber",
                            name: "emergencyPhoneNumber",
                          }}
                          inputClass=" !h-[46px] !border !border-[#51CFF363] placeholder:font-light !rounded-lg !w-full !bg-transparent  !text-base  leading-6 !pl-14 text-black placeholder:text-[#8C8CA1] placeholder:font-light focus:!border-[#089BC4] focus:!outline-none"
                          buttonClass="!px-2 !rounded-l-lg "
                          dropdownClass="bg-white"
                        />
                      )}
                    />
                    {errors.emergencyPhoneNumber ? (
                      <p className="mt-2 text-sm text-[#C62828]">
                        {errors.emergencyPhoneNumber.message}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-9">
                <h3 className="flex items-center gap-2 text-lg font-medium leading-6 text-black sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <GraduationCap color={"#089BC4"} /> Academic Details
                </h3>

                <div className="mt-5 grid grid-cols-1 gap-5 sm:mt-7 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className="block text-base leading-5 text-black sm:text-lg">
                      Qualification*
                    </label>
                    <select
                      className={`px-5 mt-1.5 h-[46px]  w-full rounded-lg border border-[#51CFF363] bg-transparent  focus:border-[#089BC4] focus:outline-none appearance-none
                        
                        ${watch("qualification") ? "text-black" : "text-[#8C8CA1]"}`}
                      {...register("qualification", {
                        required: "qualification is required",
                      })}
                      defaultValue={"Select qualification"}
                    >
                      <option value="" disabled hidden>
                        Select qualification
                      </option>
                      <option value="Ph.D">Ph.D</option>
                      <option value="Master's Degree">Master's Degree</option>
                      <option value="Bachelor's Degree">Bachelor's Degree</option>
                    </select>
                    {errors.qualification && (
                      <p className="text-sm text-[#C62828]">
                        {errors.qualification.message}
                      </p>
                    )}
                  </div>

                  <FormInput
                    id="degreeTitle"
                    label="Degree Title*"
                    type="text"
                    placeholder="e.g., Computer Science"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.degreeTitle?.message}
                    {...register("degreeTitle", {
                      required: "Degree Title is required",
                    })}
                  />

                  <FormInput
                    id="graduationYear*"
                    label="Graduation Year*"
                    type="text"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.dateOfBirth?.message}
                    {...register("graduationYear", {
                      required: "Graduation Year is required",
                    })}
                  />
                </div>

                <div className="mt-7 grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2 lg:grid-cols-3">
                  <FormInput
                    id="totalMarks"
                    label="Total CGPA/Marks"
                    type="text"
                    placeholder="e.g., 4.00/ 1100"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.totalMarks?.message}
                    {...register("totalMarks", {
                      required: "Marks is required",
                    })}
                  />
                  <FormInput
                    id="obtainedMarks"
                    label="Obtained CGPA/Marks*"
                    type="text"
                    placeholder="e.g., 3.23/880"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.obtainedMarks?.message}
                    {...register("obtainedMarks", {
                      required: "Marks is required",
                    })}
                  />

                  <FormInput
                    id="institution"
                    label="Institution*"
                    type="text"
                    labelClassName="block text-base text-black sm:text-lg "
                    inputClassName="px-5 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.institution?.message}
                    {...register("institution", {
                      required: "Institution is required",
                    })}
                  />
                </div>

                <div className="mt-7">
                  <label className="block text-base leading-5 text-black sm:text-lg">
                    Upload Transcript*
                  </label>
                  <label className="mt-1.5 flex cursor-pointer items-center justify-center rounded-lg border border-[#51CFF363] px-4 py-6 transition-colors hover:border-[#089BC4] hover:bg-[#089BC414] sm:px-6 sm:pt-8 sm:pb-10">
                    <div className="text-center">
                      <div className="mx-auto mb-3 flex h-20 w-20 items-center justify-center rounded-full bg-[#089BC414] sm:h-[100px] sm:w-[100px]">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#089BC4] sm:h-12 sm:w-12">
                          <Upload />
                        </div>
                      </div>

                      {transcriptFile?.[0] && !errors.transcript ? (
                        <>
                          <p className="text-base font-medium leading-5 text-[#089BC4] sm:text-xl">
                            {transcriptFile[0].name}
                          </p>
                          <p className="mt-1.5 text-sm leading-5 text-[#4B5563] sm:text-lg">
                            {(transcriptFile[0].size / (1024 * 1024)).toFixed(
                              2,
                            )}{" "}
                            MB
                          </p>
                          <p className="mt-2 text-sm text-[#16A34A]">
                            ✓ Transcript uploaded successfully
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-base font-light leading-5 text-black sm:text-xl">
                            Click to upload transcript
                          </p>
                          <p className="mt-1.5 text-sm leading-5 text-black sm:text-lg">
                            PDF up to 10MB
                          </p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      {...register("transcript", {
                        required: "Transcript is required",
                        validate: {
                          isPDF: (files) =>
                            !files?.length ||
                            files[0].type === "application/pdf" ||
                            "File must be a PDF",

                          fileSize: (files) =>
                            !files?.length ||
                            files[0].size <= 10 * 1024 * 1024 ||
                            "File size must be less than 10MB",
                        },
                      })}
                    />
                  </label>
                  {errors.transcript && (
                    <p className="mt-2 text-sm text-[#C62828]">
                      {errors.transcript.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-5 pt-6 sm:px-6 sm:pb-5 sm:pt-8 lg:px-10 lg:pt-11">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-medium leading-6 text-black sm:mb-6 sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <LockIcon color="#089BC4" /> Teaching Course Preferences
                </h3>

                <div>
                  <label className="mb-2 block text-base leading-5 text-black sm:text-lg">
                    Preferred Courses*
                  </label>
                  <div className="relative">
                    <select
                      className="px-5 h-[46px] bg-transparent w-full rounded-lg border border-[#51CFF363] focus:border-[#089BC4] focus:outline-none appearance-none font-light text-[#8C8CA1]"
                      value={currentCourse}
                      onChange={(e) => setCurrentCourse(e.target.value)}
                    >
                      <option value="" disabled hidden>
                        {isPublicCoursesLoading
                          ? "Loading courses..."
                          : "Select from drop down"}
                      </option>
                      {publicCourses.map((course) => (
                        <option key={course._id} value={course._id}>
                          {course.courseTitle}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <FilledDropDown />
                    </div>
                  </div>

                  <input
                    type="hidden"
                    {...register("coursePreferences", {
                      validate: (value) =>
                        (Array.isArray(value) && value.length > 0) ||
                        "Please select at least one course",
                    })}
                  />

                  {errors.coursePreferences && (
                    <p className="mt-2 text-sm text-[#C62828]">
                      {errors.coursePreferences.message}
                    </p>
                  )}

                  {isPublicCoursesError ? (
                    <p className="mt-2 text-sm text-[#C62828]">
                      Unable to load courses right now.
                    </p>
                  ) : null}

                  {selectedCourseTitles.length > 0 && (
                    <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                      {selectedCourseTitles.map((course) => (
                        <div
                          key={course._id}
                          className="flex items-center justify-between gap-2 rounded-lg border border-[#51CFF363] bg-transparent pl-2 text-sm text-black sm:text-base"
                        >
                          <span className="min-w-0 truncate pr-2">
                            {course.courseTitle}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedCourses((prev) =>
                                prev.filter((c) => c !== course._id),
                              )
                            }
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#51CFF363] bg-[#E5E5E5] text-[#25252B] sm:h-12 sm:w-12"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          currentCourse &&
                          !selectedCourses.includes(currentCourse) &&
                          selectedCourses.length < 5
                        ) {
                          setSelectedCourses((prev) => [
                            ...prev,
                            currentCourse,
                          ]);
                          setCurrentCourse("");
                        }
                      }}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#51CFF363] bg-[#089BC4] px-5 py-2.5 text-base font-medium leading-5 text-white hover:bg-[#0785A0] sm:w-auto sm:text-xl"
                    >
                      <Plus />
                      Add Another
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-[23px] border border-[#D9D9D9] bg-[#F9F6F6] px-4 pb-6 pt-6 sm:px-6 sm:pb-8 sm:pt-8 lg:px-10 lg:pb-10 lg:pt-11">
                <h3 className="mb-6 flex items-center gap-2 text-lg font-medium leading-6 text-black sm:mb-9 sm:gap-2.5 sm:text-xl sm:leading-[26px] lg:text-[22px]">
                  <LockIcon color="#089BC4" /> Account Security
                </h3>

                <div className="grid grid-cols-1 gap-5 sm:gap-7 md:grid-cols-2">
                  <FormInput
                    id="password"
                    label="Password*"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 pr-12 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363] placeholder:font-light  placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.password?.message}
                    rightContent={
                      <button
                        type="button"
                        onClick={() => setShowPassword((current) => !current)}
                        className="pointer-events-auto text-[#8C8CA1] hover:text-[#089BC4]"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                    {...register("password", {
                      required: "Password is required",
                      validate: {
                        length: (value) =>
                          /^.{8,15}$/.test(value) ||
                          "Password must be 8-15 characters",
                        letter: (value) =>
                          /[A-Za-z]/.test(value) ||
                          "Password must contain at least one letter",
                        number: (value) =>
                          /\d/.test(value) ||
                          "Password must contain at least one number",
                        special: (value) =>
                          /[^A-Za-z0-9]/.test(value) ||
                          "Password must contain at least one special character",
                        match: (value) => {
                          const confirm = getValues("confirmSecPassword");
                          if (!confirm) return true;
                          return value === confirm || "Passwords do not match";
                        },
                      },
                    })}
                  />

                  <FormInput
                    id="confirmSecPassword"
                    label="Confirm Password*"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Re-enter password"
                    labelClassName="block text-base text-black sm:text-lg"
                    inputClassName="px-5 pr-12 mt-1.5 h-[46px] rounded-lg border border-[#51CFF363]  placeholder:font-light placeholder:text-[#8C8CA1] focus:border-[#089BC4] focus:outline-none"
                    errorMessage={errors.confirmSecPassword?.message}
                    rightContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword((current) => !current)
                        }
                        className="pointer-events-auto text-[#8C8CA1] hover:text-[#089BC4]"
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    }
                    {...register("confirmSecPassword", {
                      required: "Please confirm your password",
                      validate: (value) => {
                        const currentPassword = getValues("password");
                        return (
                          value === currentPassword || "Passwords do not match"
                        );
                      },
                    })}
                  />
                </div>
              </div>

              <label className="mt-6 flex items-start gap-2.5 sm:mt-8">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[#089BC4]"
                  {...register("agreeTerms", {
                    validate: (value) =>
                      value || "Please accept Terms & Conditions",
                  })}
                />
                <span className="font-['Work_Sans'] text-sm leading-[21px] text-black">
                  I agree to the Terms & Conditions and Privacy Policy.
                </span>
              </label>
              {errors.agreeTerms ? (
                <p className="mt-2 font-['Work_Sans'] text-sm font-medium text-[#C62828]">
                  {errors.agreeTerms.message}
                </p>
              ) : null}

              <Button
                type="submit"
                disabled={isSubmitting || isRegisteringInstructor}
                btnText={
                  isSubmitting || isRegisteringInstructor
                    ? "Submitting..."
                    : "Sign Up as Instructor"
                }
                btnClassName="mt-5 w-full rounded-lg bg-[#089BC4] px-6 py-2.5 text-lg font-medium text-white transition-colors hover:bg-[#0785A0] disabled:opacity-70 sm:mt-6 sm:text-xl"
              />
            </form>

            {registerErrorMsg ? (
              <p className="mt-4 text-sm font-medium text-[#C62828]">
                {registerErrorMsg}
              </p>
            ) : null}

            <p className="mt-5 text-center text-base text-black sm:mt-6 sm:text-lg">
              Already have an account?{" "}
              {isSubmitting || isRegisteringInstructor ? (
                <span className="cursor-not-allowed font-medium text-[#089BC4] opacity-50">
                  Login here
                </span>
              ) : (
                <Link to="/login" className="font-medium text-[#089BC4]">
                  Login here
                </Link>
              )}
            </p>
          </div>
        </div>
      </div>

      {showOtpModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-[420px] rounded-[18px] bg-[#F5F5F5] px-5 pb-10 pt-8 font-['Work_Sans'] sm:px-8 sm:pb-14 sm:pt-12 lg:px-12">
            <h2 className="text-center text-xl font-semibold leading-7 tracking-[-2%] text-black sm:text-2xl">
              Enter OTP
            </h2>
            <p className="mt-3 text-center font-medium text-sm leading-4 text-black">
              We have sent a OTP to your Email
            </p>
            <p className="mt-3 text-xs  leading-5 text-black">
              Enter 4-digit OTP
            </p>

            <div className=" flex items-center justify-center gap-5 sm:gap-8 ">
              {otpValues.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    otpInputsRef.current[index] = element;
                  }}
                  type="text"
                  value={digit}
                  onChange={(event) =>
                    handleOtpChange(index, event.target.value)
                  }
                  onKeyDown={(event) => handleOtpKeyDown(index, event)}
                  inputMode="numeric"
                  maxLength={1}
                  className="h-11 w-12 rounded-lg border-[0.5px] border-[#089BC4] bg-[#FAFCFE] text-center text-xl font-semibold text-[#131A2C] focus:border-[#089BC4] focus:outline-none sm:h-12 sm:w-14 sm:text-2xl"
                />
              ))}
            </div>

            {otpError ? (
              <p className="mt-4 text-center text-sm font-medium text-[#C62828]">
                {otpError}
              </p>
            ) : null}

            {otpInfoMessage ? (
              <p className="mt-4 text-center text-sm font-medium text-[#2E7D32]">
                {otpInfoMessage}
              </p>
            ) : null}

            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={isVerifyingOtp}
              className="mt-3 h-9 w-full rounded-full bg-[#089BC4]  font-medium text-white tracking-[4%]"
            >
              {isVerifyingOtp ? "Verifying..." : "Verify"}
            </button>

            {/* <p className="mt-4  text-center text-base leading-[18px] text-[#98A2B3]">
              Didn’t receive code?{" "}
              <button
                type="button"
                onClick={handleResendOtp}
                className=" text-[#089BC4]"
              >
                Resend OTP
              </button>
            </p> */}

            <p className="mt-2 text-center text-xs text-[#98A2B3]">
              {email ? `OTP for: ${email}` : ""}
            </p>
          </div>
        </div>
      ) : null}

      {showVerifiedModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="flex min-h-[280px] w-full max-w-[420px] flex-col items-center justify-center rounded-[18px] bg-[#F5F5F5] px-6 py-10 sm:h-[360px] sm:py-14">
            <h2 className="text-center text-xl font-semibold tracking-[-2%] text-black sm:text-2xl">
              {verifiedMessage}
            </h2>

            <button
              type="button"
              onClick={handleVerifiedContinue}
              className=" mt-8 block h-8 w-full max-w-[255px] rounded-full bg-[#089BC4] leading-5 font-medium text-white"
            >
              Go To Login
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default InstructorRegistration;
